<template>
  <div>
    <el-tabs v-model="activeTab" type="card">
      <!-- 技能标签字典 -->
      <el-tab-pane label="技能标签字典" name="skillTags">
        <el-card>
          <div slot="header">
            <span style="font-weight: bold">技能标签字典</span>
          </div>

          <el-alert
            title="这里配置后，前台编辑资料页会自动同步可选技能标签。支持每行一个标签，或使用英文/中文逗号分隔，也可以直接填写 JSON 数组。"
            type="info"
            show-icon
            :closable="false"
            style="margin-bottom: 16px"
          ></el-alert>

          <el-form label-width="120px" style="max-width: 860px">
            <el-form-item label="当前标签">
              <div v-if="skillTagList.length" style="line-height: 32px">
                <el-tag
                  v-for="(tag, index) in skillTagList"
                  :key="`${tag}-${index}`"
                  size="small"
                  style="margin: 0 8px 8px 0"
                >
                  {{ tag }}
                </el-tag>
              </div>
              <span v-else style="color: #909399">暂无标签，请在下方配置。</span>
            </el-form-item>

            <el-form-item label="标签配置">
              <el-input
                v-model="skillTagConfig.editValue"
                type="textarea"
                :rows="10"
                style="width: 600px"
                placeholder="每行一个标签，或使用英文/中文逗号分隔"
              ></el-input>
              <div style="margin-top: 8px; color: #909399">
                例如：家电维修、保洁收纳、跑腿代办、摄影摄像
              </div>
            </el-form-item>

            <el-form-item>
              <el-button
                type="primary"
                size="small"
                :loading="skillTagConfig.saving"
                :disabled="String(skillTagConfig.editValue) === normalizeTagText(skillTagConfig.configValue)"
                @click="handleSaveItem(skillTagConfig)"
              >保存技能标签</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-tab-pane>

      <!-- 系统配置 -->
      <el-tab-pane label="系统配置" name="config">
        <div v-loading="configLoading" style="min-height: 200px">
          <el-empty v-if="!configLoading && groupedConfigs.length === 0" description="暂无配置数据"></el-empty>
          <el-card v-for="group in groupedConfigs" :key="group.groupCode" style="margin-bottom: 16px">
          <div slot="header">
            <span style="font-weight: bold">{{ group.groupLabel }}</span>
          </div>
          <el-form label-width="220px" style="max-width: 750px">
            <el-form-item
              v-for="item in group.configs"
              :key="item.id"
              :label="getConfigLabel(item)"
            >
              <div style="display: flex; align-items: center; width: 100%">
                <!-- BOOLEAN 类型 -->
                <template v-if="item.configType === 'BOOLEAN'">
                  <el-switch v-model="item.editValue"></el-switch>
                  <span style="margin-left: 10px; color: #909399">{{ item.description }}</span>
                </template>

                <!-- NUMBER 类型 -->
                <template v-else-if="item.configType === 'NUMBER'">
                  <el-input-number
                    v-model="item.editValue"
                    :precision="0"
                    :min="0"
                    style="width: 200px"
                  ></el-input-number>
                  <span style="margin-left: 10px; color: #909399">{{ item.description }}</span>
                </template>

                <!-- 敏感字段（密钥、证书路径等） -->
                <template v-else-if="isSensitive(item.configKey)">
                  <el-input
                    v-model="item.editValue"
                    :type="item.showValue ? 'text' : 'password'"
                    style="width: 380px"
                    placeholder="请输入"
                  >
                    <i
                      slot="suffix"
                      :class="item.showValue ? 'el-icon-view' : 'el-icon-loading'"
                      style="cursor: pointer; display: flex; align-items: center; height: 100%"
                      @click="item.showValue = !item.showValue"
                    >
                      <span v-if="item.showValue">🙈</span>
                      <span v-else>👁</span>
                    </i>
                  </el-input>
                  <span style="margin-left: 10px; color: #909399; white-space: nowrap">{{ item.description }}</span>
                </template>

                <!-- 标签配置 -->
                <template v-else-if="isTagOptions(item.configKey)">
                  <el-input
                    v-model="item.editValue"
                    type="textarea"
                    :rows="6"
                    style="width: 380px"
                    placeholder="每行一个标签，或使用英文/中文逗号分隔"
                  ></el-input>
                  <span style="margin-left: 10px; color: #909399">{{ item.description }}</span>
                </template>

                <!-- 普通 STRING 类型 -->
                <template v-else>
                  <el-input v-model="item.editValue" style="width: 380px" placeholder="请输入"></el-input>
                  <span style="margin-left: 10px; color: #909399">{{ item.description }}</span>
                </template>

                <!-- 保存按钮 -->
                <el-button
                  type="primary"
                  size="small"
                  :loading="item.saving"
                :disabled="String(item.editValue) === (isTagOptions(item.configKey) ? normalizeTagText(item.configValue) : item.configValue)"
                style="margin-left: 12px; flex-shrink: 0"
                @click="handleSaveItem(item)"
              >保存</el-button>
              </div>
            </el-form-item>
          </el-form>
        </el-card>
        </div>
      </el-tab-pane>

    </el-tabs>

  </div>
</template>

<script>
import { getSystemConfigs, saveSkillTagOptions, updateSystemConfig } from '../../api'

const SKILL_TAG_OPTIONS_KEY = 'user.skill_tag_options'

const GROUP_LABELS = {
  PLATFORM: '平台设置',
  USER: '用户资料配置',
  POST: '帖子配置',
  PAY: '支付与收费配置',
  AI: 'AI智能配置',
  CHAT: '聊天配置'
}

// 包含敏感信息的 configKey 关键字（需要密码输入框展示）
const SENSITIVE_KEYS = ['api_key', 'cert_path', 'secret', 'password', 'token']

const CONFIG_LABELS = {
  'post.image_max_count': '帖子最多上传图片数',
  'post.content_max_length': '帖子内容最大长度',
  'ai.audit.enabled': 'AI审核开关',
  'ai.category.enabled': 'AI自动分类开关',
  'ai.polish.enabled': 'AI扩写润色开关',
  'ai.model.name': 'AI模型名称',
  'ai.polish.daily_limit': 'AI扩写每日次数限制',
  'ai.polish.input_max_length': 'AI扩写输入长度限制',
  'ai.audit.confidence_threshold': 'AI审核置信度阈值',
  'chat.websocket.enabled': '聊天WebSocket开关',
  'chat.offline_allow': '下架帖子允许聊天开关',
  'user.skill_tag_options': '技能标签字典',
  'wechat_pay_merchant_id': '微信支付商户号',
  'wechat_pay_api_key': '微信支付API密钥',
  'wechat_pay_app_id': '微信支付AppID',
  'wechat_pay_cert_path': '微信支付证书路径',
  'wechat_pay_notify_url': '微信支付回调地址'
}

const HIDDEN_CONFIG_KEYS = new Set([
  'post.require_certification',
  'post.default_valid_days',
  'post.contact_visible',
  'post.no_refund_notice',
  'chat.contact_filter.enabled',
  'platform.name',
  'order_auto_close_minutes',
  'refund_approval_paid_unverified',
  'refund_approval_verified_unconfirmed'
])

export default {
  name: 'SystemManage',
  data() {
    return {
      activeTab: 'skillTags',
      configLoading: false,
      skillTagConfig: {
        id: null,
        configKey: SKILL_TAG_OPTIONS_KEY,
        configValue: '',
        editValue: '',
        saving: false
      },
      groupedConfigs: []
    }
  },
  created() { this.fetchConfigs() },
  computed: {
    skillTagList() {
      return this.parseTagOptions(this.skillTagConfig.editValue || this.skillTagConfig.configValue)
    }
  },
  methods: {
    async fetchConfigs() {
      this.configLoading = true
      try {
        const res = await getSystemConfigs({ pageNum: 1, pageSize: 200 })
        const records = (res.data && res.data.records) ? res.data.records : (Array.isArray(res.data) ? res.data : [])

        // 按 groupCode 分组
        const groupMap = {}
        records.forEach(item => {
          if (item.configKey === 'ai.polish.enabled' || HIDDEN_CONFIG_KEYS.has(item.configKey)) {
            return
          }
          if (item.configKey === SKILL_TAG_OPTIONS_KEY) {
            this.skillTagConfig = {
              ...item,
              editValue: this.normalizeTagText(item.configValue),
              saving: false
            }
            return
          }
          const group = item.groupCode || 'OTHER'
          if (!groupMap[group]) {
            groupMap[group] = []
          }
          // 根据 configType 初始化 editValue
          let editValue = item.configValue
          if (item.configType === 'BOOLEAN') {
            editValue = item.configValue === 'true' || item.configValue === '1'
          } else if (item.configType === 'NUMBER') {
            editValue = parseFloat(item.configValue) || 0
          }
          groupMap[group].push({
            ...item,
            editValue,
            showValue: false,
            saving: false
          })
        })

        // 按固定顺序构建分组列表
        const groupOrder = ['PLATFORM', 'USER', 'POST', 'PAY', 'AI', 'CHAT']
        this.groupedConfigs = []
        groupOrder.forEach(code => {
          if (groupMap[code] && groupMap[code].length > 0) {
            this.groupedConfigs.push({
              groupCode: code,
              groupLabel: GROUP_LABELS[code] || code,
              configs: groupMap[code]
            })
          }
        })
        // 处理未在预定义顺序中的分组
        Object.keys(groupMap).forEach(code => {
          if (!groupOrder.includes(code)) {
            this.groupedConfigs.push({
              groupCode: code,
              groupLabel: code,
              configs: groupMap[code]
            })
          }
        })
      } catch (e) {
        console.error('获取系统配置失败', e)
        this.$message.error('加载系统配置失败')
        this.groupedConfigs = []
      } finally {
        this.configLoading = false
      }
    },

    isSensitive(configKey) {
      return SENSITIVE_KEYS.some(k => configKey.toLowerCase().includes(k))
    },

    isTagOptions(configKey) {
      return configKey === SKILL_TAG_OPTIONS_KEY
    },

    normalizeTagText(rawValue) {
      const tags = this.parseTagOptions(rawValue)
      return tags.join('\n')
    },

    parseTagOptions(rawValue) {
      if (!rawValue) return []
      if (Array.isArray(rawValue)) {
        return rawValue.map(item => String(item).trim()).filter(Boolean)
      }

      const text = String(rawValue).trim()
      if (!text) return []

      if (text.startsWith('[')) {
        try {
          const parsed = JSON.parse(text)
          if (Array.isArray(parsed)) {
            return parsed.map(item => String(item).trim()).filter(Boolean)
          }
        } catch (e) {
          // Fall back to delimiter parsing below.
        }
      }

      return text.split(/[\n,，]/).map(item => item.trim()).filter(Boolean)
    },

    getConfigLabel(item) {
      return item.description || CONFIG_LABELS[item.configKey] || '配置项'
    },

    async handleSaveItem(item) {
      let saveValue = item.editValue
      // BOOLEAN 转为字符串
      if (item.configType === 'BOOLEAN') {
        saveValue = saveValue ? 'true' : 'false'
      }
      saveValue = String(saveValue)

      if (saveValue === item.configValue) return

      item.saving = true
      try {
        if (this.isTagOptions(item.configKey)) {
          const res = await saveSkillTagOptions({ configValue: saveValue })
          const saved = res && res.data ? res.data : {}
          if (saved.id != null) item.id = saved.id
          item.configValue = saved.configValue != null ? saved.configValue : saveValue
          item.configType = saved.configType || item.configType || 'STRING'
          item.groupCode = saved.groupCode || item.groupCode || 'USER'
          item.description = saved.description || item.description
          item.editValue = this.normalizeTagText(saveValue)
        } else {
          await updateSystemConfig(item.id, { configValue: saveValue })
          item.configValue = saveValue
        }
        this.$message.success('保存成功')
      } catch {
        // 恢复原值
        this.resetEditValue(item)
      } finally {
        item.saving = false
      }
    },

    resetEditValue(item) {
      if (item.configType === 'BOOLEAN') {
        item.editValue = item.configValue === 'true' || item.configValue === '1'
      } else if (item.configType === 'NUMBER') {
        item.editValue = parseFloat(item.configValue) || 0
      } else {
        item.editValue = item.configValue
      }
    },

  }
}
</script>
